/**
 * Module: `export/export-filename.ts` — Nom de fichier d'export for PDF/Word.
 * Spec: docs/adr/0004-nom-fichier-export-reponse.md
 */

import { flattenQuestionTextForExport } from "@/lib/export/build-response-export-document";
import type { ResponseExportFormat } from "@/lib/export/response-export-document";
import { findUserQuestionForAssistantMessage } from "@/lib/messages/find-user-question-for-assistant";
import { isQuestionUserMessage } from "@/lib/qualification-lifecycle/metadata-part";
import type { Message } from "@/types";
import type { MessageId } from "@/types/branded";

/** Prefix shared by every response export download. */
export const EXPORT_FILENAME_PREFIX = "sidoni-reponse";

/**
 * Hard ceiling for the whole filename (name + extension).
 * Keeps a safety margin under the common OS 255-byte component limit.
 */
export const EXPORT_FILENAME_MAX_LENGTH = 200;

/** Soft target for the question slug (whole words, ASCII). */
export const EXPORT_FILENAME_SLUG_TARGET_LENGTH = 50;

export interface BuildExportFilenameInput {
  format: ResponseExportFormat;
  questionNumber?: number | null;
  questionText?: string | null;
  questionDate?: Date | string | null;
  answerDate?: Date | string | null;
  exportedAt?: Date | string | null;
}

function toDate(value: Date | string | null | undefined): Date | null {
  if (value == null) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Local calendar day `AAAA-MM-JJ` (user timezone). */
export function formatLocalDateYmd(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Date segment: question → answer → export day (local).
 */
export function resolveExportFilenameDate({
  questionDate,
  answerDate,
  exportedAt = new Date(),
}: Pick<
  BuildExportFilenameInput,
  "questionDate" | "answerDate" | "exportedAt"
>): string {
  const resolved =
    toDate(questionDate) ??
    toDate(answerDate) ??
    toDate(exportedAt) ??
    new Date();
  return formatLocalDateYmd(resolved);
}

/**
 * ASCII slug from question text: whole words, no mid-word cut at the soft target.
 */
export function slugifyQuestionForFilename(
  questionText: string | null | undefined,
  maxLength = EXPORT_FILENAME_SLUG_TARGET_LENGTH
): string | null {
  if (!questionText?.trim()) return null;

  const flattened = flattenQuestionTextForExport(questionText);
  const ascii = flattened
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  if (!ascii) return null;

  if (ascii.length <= maxLength) return ascii;

  const truncated = ascii.slice(0, maxLength);
  const lastHyphen = truncated.lastIndexOf("-");
  const wholeWords =
    lastHyphen > 0 ? truncated.slice(0, lastHyphen) : truncated;
  return wholeWords.replace(/-+$/g, "") || null;
}

/**
 * Rank of the linked Question à l'assistant in the transcript (1-based).
 * Same criterion as the sidebar / ChatMessageList.
 */
export function findQuestionNumberForAssistantMessage(
  messages: Message[],
  assistantMessageId: MessageId | string
): number | null {
  const question = findUserQuestionForAssistantMessage(
    messages,
    assistantMessageId
  );
  if (!question) return null;

  let rank = 0;
  for (const message of messages) {
    if (!isQuestionUserMessage(message)) continue;
    rank += 1;
    if (String(message.id) === String(question.id)) {
      return rank;
    }
  }
  return null;
}

function joinFilenameParts(parts: string[], extension: string): string {
  return `${parts.join("-")}.${extension}`;
}

/**
 * Builds a Nom de fichier d'export:
 * `sidoni-reponse[-QN][-slug]-AAAA-MM-JJ.(pdf|docx)`
 */
export function buildExportFilename({
  format,
  questionNumber,
  questionText,
  questionDate,
  answerDate,
  exportedAt = new Date(),
}: BuildExportFilenameInput): string {
  const dateStamp = resolveExportFilenameDate({
    questionDate,
    answerDate,
    exportedAt,
  });
  const extension = format;
  const fixedWithoutSlug = [
    EXPORT_FILENAME_PREFIX,
    questionNumber != null && questionNumber > 0 ? `Q${questionNumber}` : null,
    dateStamp,
  ].filter((part): part is string => Boolean(part));

  const reserved =
    joinFilenameParts(fixedWithoutSlug, extension).length +
    1; /* hyphen before slug when present */
  const slugBudget = Math.max(
    8,
    Math.min(
      EXPORT_FILENAME_SLUG_TARGET_LENGTH,
      EXPORT_FILENAME_MAX_LENGTH - reserved
    )
  );
  const slug = slugifyQuestionForFilename(questionText, slugBudget);

  const parts = [
    EXPORT_FILENAME_PREFIX,
    questionNumber != null && questionNumber > 0 ? `Q${questionNumber}` : null,
    slug,
    dateStamp,
  ].filter((part): part is string => Boolean(part));

  let filename = joinFilenameParts(parts, extension);
  if (filename.length <= EXPORT_FILENAME_MAX_LENGTH) {
    return filename;
  }

  // Last resort: drop the slug to stay under the OS-safe ceiling.
  filename = joinFilenameParts(fixedWithoutSlug, extension);
  if (filename.length <= EXPORT_FILENAME_MAX_LENGTH) {
    return filename;
  }

  // Extreme fallback (should not happen with current prefix/date sizes).
  return joinFilenameParts([EXPORT_FILENAME_PREFIX, dateStamp], extension);
}
